import type { User } from '@/types/user';
import { userHasScope } from './userHasScope';

const staffUserManagers = ['superuser', 'acenda_the_management', 'acenda_admin'] as const;
const definitionManagers = ['superuser', 'acenda_the_management'] as const;
const hiddenOrganizationRoles = new Set([
  'acenda_admin',
  'acenda_the_management',
  'api-client',
  'apiclient',
  'daily_dashboard',
  'superuser',
  'uma_protection',
  'user',
]);

export type OrganizationAccess = {
  staffUserManager: boolean;
  definitionManager: boolean;
  acendaAdmin: boolean;
  owner: boolean;
  admin: boolean;
};

export const organizationAccess = (realmRoles: readonly string[], organizationRoles: readonly string[]): OrganizationAccess => ({
  staffUserManager: staffUserManagers.some((role) => realmRoles.includes(role)),
  definitionManager: definitionManagers.some((role) => realmRoles.includes(role)),
  acendaAdmin: realmRoles.includes('acenda_admin'),
  owner: organizationRoles.includes('owner'),
  admin: organizationRoles.includes('admin'),
});

export const canManageOrganizationUsers = (access: OrganizationAccess): boolean => access.staffUserManager || access.owner || access.admin;

export const canManageOrganizationUser = (
  access: OrganizationAccess,
  subject: string | null,
  targetID: string,
  targetRoles: readonly string[]
): boolean => {
  if (!subject || subject === targetID || !canManageOrganizationUsers(access)) return false;
  return !targetRoles.includes('owner') || access.staffUserManager || access.owner;
};

export const canRemoveOrganizationUser = (
  access: OrganizationAccess,
  subject: string | null,
  targetID: string,
  targetRoles: readonly string[]
): boolean => {
  if (!canManageOrganizationUser(access, subject, targetID, targetRoles)) return false;
  return !targetRoles.includes('owner') || access.staffUserManager;
};

export const canManageAuthorizationDefinitions = (access: OrganizationAccess): boolean => access.definitionManager;

export const canUseOrganizationAPIClients = (realmRoles: readonly string[]): boolean =>
  realmRoles.includes('superuser') || (!realmRoles.includes('acenda_admin') && !realmRoles.includes('acenda_the_management'));

const organizationAdministrationTabs = [
  { id: 'users', label: 'Users', scope: 'user:read' },
  { id: 'authorization', label: 'Authorization', scope: 'access_control:read' },
  { id: 'api-keys', label: 'API Keys', scope: 'api_client:read' },
] as const;

export const availableOrganizationAdministrationTabs = (user: User | null, realmRoles: readonly string[]) =>
  organizationAdministrationTabs.filter(
    (tab) =>
      userHasScope(user, tab.scope) &&
      (tab.id !== 'authorization' || definitionManagers.some((role) => realmRoles.includes(role))) &&
      (tab.id !== 'api-keys' || canUseOrganizationAPIClients(realmRoles))
  );

export const isAssignableOrganizationRole = (role: string): boolean => ['owner', 'admin', 'employee'].includes(role);

export const isVisibleOrganizationRole = (role: string): boolean => !hiddenOrganizationRoles.has(role) && !role.startsWith('api_key_');

export const canRevokeOrganizationRole = (access: OrganizationAccess, role: string): boolean => role !== 'owner' || access.staffUserManager;

export const organizationRoleNames = (roles: unknown): string[] => {
  if (Array.isArray(roles)) return roles.filter((role): role is string => typeof role === 'string' && role !== '');
  if (typeof roles !== 'string') return [];
  return roles.split(',').filter(Boolean);
};
